import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SubmitButton } from "@/components/auth/AuthLayout";
import { useCaptcha, CaptchaField } from "@/components/auth/Captcha";
import { api, errMsg } from "@/lib/api";
import { ResetCodeForm, ChannelPicker } from "@/components/auth/ResetCodeForm";

export const PasswordInput = ({ value, onChange, testId, autoComplete = "current-password" }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} autoComplete={autoComplete} className="h-11 pr-10" data-testid={testId} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-2.5 text-gray-400 hover:text-[#111]" aria-label={show ? "Hide password" : "Show password"} data-testid={`${testId}-toggle`}>
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
};

const ForgotForm = ({ account, onClose }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState("email");
  const codes = account !== "staff";
  const captcha = useCaptcha(account === "staff" ? "image" : "math");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email, account, channel, ...captcha.fields }); setSent(true); } catch (err) { toast.error(errMsg(err)); captcha.reload(); }
    setBusy(false);
  };
  if (sent && codes) return <ResetCodeForm email={email} channel={channel} onBack={() => { setSent(false); captcha.reload(); }} onDone={onClose} />;
  return sent ? <p className="text-sm text-gray-600" data-testid="forgot-sent-message">If an account uses that email, a reset link is on its way. The link expires in 1 hour.</p> : (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">{codes ? "Enter the email address on your account and choose how to receive your 6-digit reset code." : "Enter the email address on your account and we’ll send you a reset link."}</p>
      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11" required data-testid="forgot-email-input" />
      {codes && <ChannelPicker value={channel} onChange={setChannel} />}
      <CaptchaField c={captcha} testId="forgot-captcha" />
      <SubmitButton busy={busy} testId="forgot-submit-button">{busy ? "Sending…" : codes ? "Send reset code" : "Send reset link"}</SubmitButton>
      {codes && <button type="button" onClick={() => (email.trim() ? setSent(true) : toast.error("Enter your email first"))} className="text-sm font-semibold text-gray-600 hover:text-[#E60000]" data-testid="forgot-have-code">I already have a code</button>}
    </form>
  );
};

export const ForgotDialog = ({ open, onClose, account = "any" }) => (
  <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
    <DialogContent className="max-w-md" data-testid="forgot-dialog">
      <DialogHeader><DialogTitle className="font-heading">Reset your password</DialogTitle></DialogHeader>
      {open && <ForgotForm account={account} onClose={onClose} />}
    </DialogContent>
  </Dialog>
);
